import { toCanvas } from "html-to-image"
import { jsPDF } from "jspdf"

const PDF_WIDTH_MM = 210
const DESIGN_W = 1200
const PAGE_BACKGROUND = "#0d081c"
const MAX_CANVAS_AREA = 16_777_216
const MAX_CANVAS_SIDE = 8192

interface ImageOverlay {
  canvas: HTMLCanvasElement
  x: number
  y: number
  w: number
  h: number
}

/**
 * Rasterize the 1200px report artboard into a single-page PDF.
 *
 * html-to-image uses SVG foreignObject — iOS does not paint <img> there.
 * Keep image slots in the snapshot (opacity 0), then composite photos under
 * existing text so titles/captions are not covered.
 */
export async function generateAuraScanPdf(root: HTMLElement): Promise<Blob> {
  const unfreeze = freezeArtboard(root)
  try {
    const height = Math.max(root.scrollHeight, root.offsetHeight)
    const overlays = await buildImageOverlays(root)
    const restoreBlend = flattenBlendModes(root)
    const unhide = hideImages(root)
    try {
      const canvas = await toCanvas(root, {
        cacheBust: false,
        pixelRatio: capturePixelRatio(DESIGN_W, height),
        backgroundColor: PAGE_BACKGROUND,
        width: DESIGN_W,
        height,
        style: {
          transform: "none",
          width: `${DESIGN_W}px`,
          height: `${height}px`,
          left: "0",
          top: "0",
        },
      })
      paintOverlaysUnderText(canvas, overlays, DESIGN_W, height)
      const jpeg = canvasToJpeg(canvas)
      const pdfHeight = PDF_WIDTH_MM * (canvas.height / canvas.width)
      const pdf = new jsPDF({
        unit: "mm",
        format: [PDF_WIDTH_MM, pdfHeight],
        orientation: pdfHeight >= PDF_WIDTH_MM ? "portrait" : "landscape",
      })
      pdf.addImage(jpeg, "JPEG", 0, 0, PDF_WIDTH_MM, pdfHeight)
      return pdf.output("blob")
    } finally {
      unhide()
      restoreBlend()
    }
  } finally {
    unfreeze()
  }
}

function freezeArtboard(root: HTMLElement): () => void {
  const previous = root.style.transform
  root.style.transform = "none"
  return () => {
    root.style.transform = previous
  }
}

function hideImages(root: HTMLElement): () => void {
  const restore = [...root.querySelectorAll("img")].map((img) => {
    const previous = img.style.opacity
    img.style.opacity = "0"
    return () => {
      img.style.opacity = previous
    }
  })
  return () => restore.forEach((fn) => fn())
}

function paintOverlaysUnderText(
  target: HTMLCanvasElement,
  overlays: ImageOverlay[],
  layoutW: number,
  layoutH: number,
): void {
  const ctx = target.getContext("2d")
  if (!ctx || !layoutW || !layoutH) return
  const sx = target.width / layoutW
  const sy = target.height / layoutH
  for (const overlay of overlays) {
    const x = Math.round(overlay.x * sx)
    const y = Math.round(overlay.y * sy)
    const w = Math.max(1, Math.round(overlay.w * sx))
    const h = Math.max(1, Math.round(overlay.h * sy))
    if (x >= target.width || y >= target.height) continue
    const dw = Math.min(w, target.width - Math.max(0, x))
    const dh = Math.min(h, target.height - Math.max(0, y))
    if (dw < 1 || dh < 1) continue
    const dx = Math.max(0, x)
    const dy = Math.max(0, y)

    const tmp = document.createElement("canvas")
    tmp.width = dw
    tmp.height = dh
    const tctx = tmp.getContext("2d")
    if (!tctx) continue
    tctx.drawImage(overlay.canvas, 0, 0, dw, dh)

    const photo = tctx.getImageData(0, 0, dw, dh)
    const snap = ctx.getImageData(dx, dy, dw, dh)
    const pd = photo.data
    const sd = snap.data
    for (let i = 0; i < sd.length; i += 4) {
      if (isBackdropPixel(sd[i], sd[i + 1], sd[i + 2], sd[i + 3])) {
        sd[i] = pd[i]
        sd[i + 1] = pd[i + 1]
        sd[i + 2] = pd[i + 2]
        sd[i + 3] = 255
      }
    }
    ctx.putImageData(snap, dx, dy)
  }
}

function isBackdropPixel(r: number, g: number, b: number, a: number): boolean {
  if (a < 40) return true
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luma < 52
}

async function buildImageOverlays(root: HTMLElement): Promise<ImageOverlay[]> {
  const overlays: ImageOverlay[] = []
  for (const img of root.querySelectorAll("img")) {
    const box = artboardBox(root, img)
    if (box.w < 2 || box.h < 2) continue
    const source = await loadImageSource(img)
    if (!source) continue

    const canvas = document.createElement("canvas")
    const ratio = 2
    canvas.width = Math.max(1, Math.round(box.w * ratio))
    canvas.height = Math.max(1, Math.round(box.h * ratio))
    const ctx = canvas.getContext("2d")
    if (!ctx) continue
    ctx.fillStyle = opaqueBackground(img)
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    const css = getComputedStyle(img)
    drawFitted(ctx, source, canvas.width, canvas.height, css.objectFit, css.objectPosition)
    overlays.push({ canvas, ...box })
  }
  return overlays
}

function artboardBox(root: HTMLElement, el: HTMLElement) {
  const rootRect = root.getBoundingClientRect()
  const rect = el.getBoundingClientRect()
  const scale = rootRect.width / DESIGN_W
  if (!scale) return { x: 0, y: 0, w: 0, h: 0 }
  return {
    x: (rect.left - rootRect.left) / scale,
    y: (rect.top - rootRect.top) / scale,
    w: rect.width / scale,
    h: rect.height / scale,
  }
}

function flattenBlendModes(root: HTMLElement): () => void {
  const restore = [...root.querySelectorAll<HTMLElement>("*")].flatMap((el) => {
    const mode = getComputedStyle(el).mixBlendMode
    if (!mode || mode === "normal") return []
    const previous = el.style.mixBlendMode
    el.style.mixBlendMode = "normal"
    return [
      () => {
        el.style.mixBlendMode = previous
      },
    ]
  })
  return () => restore.forEach((fn) => fn())
}

type ImageSource = HTMLImageElement | ImageBitmap

async function loadImageSource(img: HTMLImageElement): Promise<ImageSource | null> {
  const src = img.currentSrc || img.src
  if (!src) return null
  try {
    const res = await fetch(src)
    const blob = await res.blob()
    if (typeof createImageBitmap === "function") {
      return await createImageBitmap(blob)
    }
    return await blobToImage(blob)
  } catch {
    try {
      await img.decode()
      return img.naturalWidth > 0 ? img : null
    } catch {
      return null
    }
  }
}

function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const el = new Image()
    const url = URL.createObjectURL(blob)
    el.onload = () => {
      URL.revokeObjectURL(url)
      resolve(el)
    }
    el.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error("Failed to decode image"))
    }
    el.src = url
  })
}

function drawFitted(
  ctx: CanvasRenderingContext2D,
  source: ImageSource,
  dw: number,
  dh: number,
  fit: string,
  position: string,
): void {
  const sw = "naturalWidth" in source ? source.naturalWidth : source.width
  const sh = "naturalHeight" in source ? source.naturalHeight : source.height
  if (!sw || !sh) return

  const [posX, posY] = parseObjectPosition(position)
  let dwDraw = dw
  let dhDraw = dh
  let dx = 0
  let dy = 0

  if (fit === "contain") {
    const scale = Math.min(dw / sw, dh / sh)
    dwDraw = sw * scale
    dhDraw = sh * scale
    dx = (dw - dwDraw) * posX
    dy = (dh - dhDraw) * posY
  } else if (fit === "cover") {
    const scale = Math.max(dw / sw, dh / sh)
    dwDraw = sw * scale
    dhDraw = sh * scale
    dx = (dw - dwDraw) * posX
    dy = (dh - dhDraw) * posY
  } else if (fit === "none") {
    dwDraw = sw
    dhDraw = sh
    dx = (dw - dwDraw) * posX
    dy = (dh - dhDraw) * posY
  }

  ctx.drawImage(source, dx, dy, dwDraw, dhDraw)
}

function parseObjectPosition(position: string): [number, number] {
  const parts = position.trim().split(/\s+/)
  if (parts.length === 1) {
    const part = parts[0] ?? "center"
    if (part === "top") return [0.5, 0]
    if (part === "bottom") return [0.5, 1]
    if (part === "left") return [0, 0.5]
    if (part === "right") return [1, 0.5]
    const n = parsePositionPart(part, "x")
    return [n, 0.5]
  }
  return [
    parsePositionPart(parts[0] ?? "50%", "x"),
    parsePositionPart(parts[1] ?? "50%", "y"),
  ]
}

function parsePositionPart(part: string, axis: "x" | "y"): number {
  if (axis === "x") {
    if (part === "left") return 0
    if (part === "right") return 1
    if (part === "center" || part === "top" || part === "bottom") return 0.5
  } else {
    if (part === "top") return 0
    if (part === "bottom") return 1
    if (part === "center" || part === "left" || part === "right") return 0.5
  }
  if (part.endsWith("%")) return Number.parseFloat(part) / 100
  return 0.5
}

function opaqueBackground(img: HTMLImageElement): string {
  let node: HTMLElement | null = img.parentElement
  while (node) {
    const color = getComputedStyle(node).backgroundColor
    if (isOpaqueColor(color)) return color
    node = node.parentElement
  }
  return PAGE_BACKGROUND
}

function isOpaqueColor(color: string): boolean {
  if (!color || color === "transparent") return false
  const rgb = color.match(/rgba?\(([^)]+)\)/i)
  if (!rgb) return color !== "none"
  const parts = rgb[1].split(",").map((p) => p.trim())
  if (parts.length < 4) return true
  return Number.parseFloat(parts[3]) >= 0.99
}

function isIos(): boolean {
  if (typeof navigator === "undefined") return false
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1
}

function capturePixelRatio(width: number, height: number): number {
  if (!width || !height) return 1
  const preferred = isIos() ? 1 : 2
  const areaLimit = Math.sqrt(MAX_CANVAS_AREA / (width * height))
  const sideLimit = Math.min(MAX_CANVAS_SIDE / width, MAX_CANVAS_SIDE / height)
  return Math.max(0.5, Math.min(preferred, areaLimit, sideLimit))
}

function canvasToJpeg(canvas: HTMLCanvasElement): string {
  const out = document.createElement("canvas")
  out.width = canvas.width
  out.height = canvas.height
  const ctx = out.getContext("2d")
  if (!ctx) throw new Error("Could not create canvas")
  ctx.fillStyle = PAGE_BACKGROUND
  ctx.fillRect(0, 0, out.width, out.height)
  ctx.drawImage(canvas, 0, 0)
  return out.toDataURL("image/jpeg", 0.85)
}
